import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, AlertTriangle, AlertOctagon, Clock } from 'lucide-react'
import { api } from '@/lib/noc-api-client'
import { toast } from 'react-hot-toast'

interface DeviceState {
  device_serial: string
  device_name: string | null
  device_type: string
  model: string | null
  org_id: string | null
  network_id: string | null
  network_name: string | null
  current_status: string
  went_down_at: string | null
  failure_detected: boolean
  malfunction_sign: boolean
  failure_count_last_hour: number
  ticket_raised: boolean
  team_alerted: boolean
}

interface RaiseTicketModalProps {
  isOpen: boolean
  onClose: () => void
  device: DeviceState
  onSuccess: () => void
}

export function RaiseTicketModal({ isOpen, onClose, device, onSuccess }: RaiseTicketModalProps) {
  const [submitting, setSubmitting] = useState(false)

  const getDownDuration = () => {
    if (!device.went_down_at) return 'Unknown'
    const downTime = new Date(device.went_down_at)
    const now = new Date()
    const minutes = Math.floor((now.getTime() - downTime.getTime()) / 60000)
    if (minutes < 60) return `${minutes} minutes`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const response = await api.post(
        `/api/threshold-monitoring/raise-ticket/${device.device_serial}`
      )

      if (response.status === 'success') {
        toast.success(response.message)
        onSuccess()
      } else {
        toast.error(response.message || 'Failed to raise ticket')
      }
    } catch (error) {
      console.error('Failed to raise ticket:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to raise ticket'
      toast.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {device.malfunction_sign ? (
              <AlertOctagon className="h-5 w-5 text-purple-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-orange-600" />
            )}
            Raise Ticket for {device.device_name || device.device_serial}
          </DialogTitle>
          <DialogDescription>
            A ticket will be created and appear in the Alerts/Insights page, and email notifications will be sent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Device Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-[#676767] font-medium mb-1">Device Type</p>
              <Badge variant="outline">{device.device_type}</Badge>
            </div>
            <div>
              <p className="text-xs text-[#676767] font-medium mb-1">Model</p>
              <p className="text-sm text-[#1a1a1a]">{device.model || 'N/A'}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-[#676767] font-medium mb-1">Network</p>
            <p className="text-sm text-[#1a1a1a]">{device.network_name || device.network_id || 'N/A'}</p>
          </div>

          <div>
            <p className="text-xs text-[#676767] font-medium mb-1">Serial Number</p>
            <p className="text-xs font-mono text-[#676767]">{device.device_serial}</p>
          </div>

          {/* Issue Details */}
          <div className="border-t pt-4 space-y-3">
            {device.failure_detected && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-900">Failure Detected</p>
                  <p className="text-xs text-red-700">
                    Device has been down for {getDownDuration()} without recovery
                  </p>
                </div>
              </div>
            )}

            {device.malfunction_sign && (
              <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <AlertOctagon className="h-4 w-4 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-purple-900">Malfunction Sign</p>
                  <p className="text-xs text-purple-700">
                    {device.failure_count_last_hour} failures detected in the last hour
                  </p>
                </div>
              </div>
            )}

            {device.went_down_at && (
              <div className="flex items-center gap-2 text-sm text-[#676767]">
                <Clock className="h-4 w-4" />
                <span>Down since: {new Date(device.went_down_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Ticket...
              </>
            ) : (
              'Confirm & Raise Ticket'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
